// Settings > Account: display name, username, password, optional email,
// deleting the account, and (for admins) managing admins and players' coins,
// items and free orb pulls, sending messages to players, and reading players' feedback.
import { useState, type FormEvent } from "react";
import {
  ADMIN_COINS_MAX,
  ADMIN_ORBS_MAX,
  ORB_NAMES,
  ORB_TIERS,
  type OrbTier,
  DISPLAY_NAME_MAX,
  ITEMS,
  DisplayNameSchema,
  EmailSchema,
  PASSWORD_MAX,
  PASSWORD_MIN,
  PasswordSchema,
  UsernameSchema,
  type Me,
} from "@wp/shared";
import { api, type ApiResult } from "../../net/api";
import { AdminBroadcast } from "./AdminBroadcast";
import { AdminFeedback } from "./AdminFeedback";
import { AdminStats } from "./AdminStats";

interface Props {
  me: Me;
  onMeChange: (me: Me) => void;
  /** The account was deleted: back to the login screen. */
  onDeleted: () => void;
}

type Status = { kind: "ok" | "error"; text: string } | null;

function StatusLine({ status }: { status: Status }) {
  if (!status) return null;
  return (
    <p className={status.kind === "ok" ? "form-ok" : "form-error"} role={status.kind === "ok" ? "status" : "alert"}>
      {status.text}
    </p>
  );
}

export function AccountPanel({ me, onMeChange, onDeleted }: Props) {
  const [displayName, setDisplayName] = useState(me.displayName);
  const [email, setEmail] = useState(me.email ?? "");
  const [username, setUsername] = useState(me.username);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [adminName, setAdminName] = useState("");
  const [busy, setBusy] = useState(false);
  const [profileStatus, setProfileStatus] = useState<Status>(null);
  const [nameStatus, setNameStatus] = useState<Status>(null);
  const [passStatus, setPassStatus] = useState<Status>(null);
  const [deleteStatus, setDeleteStatus] = useState<Status>(null);
  const [adminStatus, setAdminStatus] = useState<Status>(null);
  const [playerName, setPlayerName] = useState("");
  const [coinsText, setCoinsText] = useState("");
  const [itemChoice, setItemChoice] = useState("all");
  const [playerStatus, setPlayerStatus] = useState<Status>(null);
  const [orbName, setOrbName] = useState("");
  const [orbTier, setOrbTier] = useState<OrbTier>("low");
  const [orbCountText, setOrbCountText] = useState("1");
  const [orbStatus, setOrbStatus] = useState<Status>(null);

  async function run<T>(call: () => Promise<ApiResult<T>>, set: (s: Status) => void, okText: string, after?: (v: T) => void) {
    setBusy(true);
    const res = await call();
    setBusy(false);
    if (res.ok) {
      set({ kind: "ok", text: okText });
      after?.(res.value);
    } else {
      set({ kind: "error", text: res.error.message });
    }
  }

  function saveProfile(e: FormEvent) {
    e.preventDefault();
    const dn = DisplayNameSchema.safeParse(displayName);
    if (!dn.success) return setProfileStatus({ kind: "error", text: `Display names are 1-${DISPLAY_NAME_MAX} characters.` });
    if (email !== "" && !EmailSchema.safeParse(email).success) {
      return setProfileStatus({ kind: "error", text: "That email does not look right." });
    }
    void run(() => api.updateProfile({ displayName: dn.data, email: email === "" ? null : email }), setProfileStatus, "Saved.", onMeChange);
  }

  function saveUsername(e: FormEvent) {
    e.preventDefault();
    if (!UsernameSchema.safeParse(username).success) {
      return setNameStatus({ kind: "error", text: "Usernames use letters, numbers, spaces, _ - . (1-30 characters)." });
    }
    void run(() => api.changeUsername(username), setNameStatus, "Username changed. Use it to log in from now on.", onMeChange);
  }

  function savePassword(e: FormEvent) {
    e.preventDefault();
    if (!PasswordSchema.safeParse(next).success) {
      return setPassStatus({ kind: "error", text: `New passwords are ${PASSWORD_MIN}-${PASSWORD_MAX} characters.` });
    }
    void run(() => api.changePassword(current, next), setPassStatus, "Password changed. Other devices were logged out.", () => {
      setCurrent("");
      setNext("");
    });
  }

  function deleteAccount(e: FormEvent) {
    e.preventDefault();
    const confirm = me.hasPassword ? { password: deleteConfirm } : { confirmUsername: deleteConfirm };
    void run(() => api.deleteAccount(confirm), setDeleteStatus, "Account deleted.", onDeleted);
  }

  function makeAdmin(e: FormEvent, isAdmin: boolean) {
    e.preventDefault();
    void run(
      () => api.setAdmin(adminName.trim(), isAdmin),
      setAdminStatus,
      isAdmin ? `${adminName.trim()} is now an admin.` : `${adminName.trim()} is no longer an admin.`,
    );
  }

  /** An admin edit of your own account shows straight away. */
  function afterPlayerEdit(p: { username: string; coins: number; unlocks: string[] }) {
    if (p.username.toLowerCase() === me.username.toLowerCase()) onMeChange({ ...me, coins: p.coins, unlocks: p.unlocks });
  }

  const coinsValue = Number(coinsText);
  const coinsValid = coinsText.trim() !== "" && Number.isSafeInteger(coinsValue) && coinsValue >= 0 && coinsValue <= ADMIN_COINS_MAX;
  const itemLabel = itemChoice === "all" ? "every item" : (ITEMS.find((i) => i.id === itemChoice)?.name ?? itemChoice);

  function setCoins(e: FormEvent) {
    e.preventDefault();
    const name = playerName.trim();
    void run(() => api.adminSetCoins(name, coinsValue), setPlayerStatus, `${name} now has ${coinsValue.toLocaleString()} coins.`, afterPlayerEdit);
  }

  function setUnlock(owned: boolean) {
    const name = playerName.trim();
    const ids = itemChoice === "all" ? "all" : [itemChoice];
    void run(
      () => api.adminSetUnlocks(name, ids, owned),
      setPlayerStatus,
      owned ? `Gave ${itemLabel} to ${name}.` : `Took ${itemLabel} from ${name}.`,
      afterPlayerEdit,
    );
  }

  const orbCount = Number(orbCountText);
  const orbCountValid = Number.isInteger(orbCount) && orbCount >= 1 && orbCount <= ADMIN_ORBS_MAX;

  function giveOrbs(e: FormEvent) {
    e.preventDefault();
    const name = orbName.trim();
    void run(
      () => api.adminGiveOrbs({ username: name, tier: orbTier, count: orbCount }),
      setOrbStatus,
      `Gave ${orbCount} free ${ORB_NAMES[orbTier]} pull${orbCount === 1 ? "" : "s"} to ${name}.`,
      (r) => {
        setOrbStatus({ kind: "ok", text: `Gave ${orbCount} free ${ORB_NAMES[orbTier]} pull${orbCount === 1 ? "" : "s"} to ${r.username}. They now have ${r.freeOrbs[orbTier]}.` });
        if (r.username.toLowerCase() === me.username.toLowerCase()) onMeChange({ ...me, freeOrbs: r.freeOrbs });
      },
    );
  }

  return (
    <div className="account-panel" data-testid="account-panel">
      <form className="stack-form group-box" onSubmit={saveProfile} noValidate>
        <h3>Profile</h3>
        <label className="field">
          <span>Display name (shown at the table)</span>
          <input value={displayName} maxLength={DISPLAY_NAME_MAX} onChange={(e) => setDisplayName(e.target.value)} data-testid="account-display-name" />
        </label>
        <label className="field">
          <span>Email (optional)</span>
          <input type="email" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} data-testid="account-email" />
        </label>
        <StatusLine status={profileStatus} />
        <div className="dialog-actions">
          <button type="submit" className="btn primary" disabled={busy} data-testid="account-save-profile">
            Save profile
          </button>
        </div>
      </form>

      <form className="stack-form group-box" onSubmit={saveUsername} noValidate>
        <h3>Username (for logging in)</h3>
        <label className="field">
          <span>Username</span>
          <input value={username} maxLength={30} autoComplete="username" onChange={(e) => setUsername(e.target.value)} />
        </label>
        <StatusLine status={nameStatus} />
        <div className="dialog-actions">
          <button type="submit" className="btn" disabled={busy || username === me.username}>
            Change username
          </button>
        </div>
      </form>

      {me.hasPassword ? (
        <form className="stack-form group-box" onSubmit={savePassword} noValidate>
          <h3>Password</h3>
          <label className="field">
            <span>Current password</span>
            <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </label>
          <label className="field">
            <span>New password</span>
            <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
          </label>
          <StatusLine status={passStatus} />
          <div className="dialog-actions">
            <button type="submit" className="btn" disabled={busy || current === "" || next === ""}>
              Change password
            </button>
          </div>
        </form>
      ) : (
        <p className="muted small group-box">You sign in with {me.oauthProvider === "google" ? "Google" : "Discord"}, so this account has no password.</p>
      )}

      {me.isAdmin && (
        <form className="stack-form group-box" onSubmit={(e) => makeAdmin(e, true)} noValidate>
          <h3>Admin: manage admins</h3>
          <label className="field">
            <span>Username</span>
            <input value={adminName} onChange={(e) => setAdminName(e.target.value)} data-testid="admin-username" />
          </label>
          <StatusLine status={adminStatus} />
          <div className="dialog-actions">
            <button type="button" className="btn" disabled={busy || adminName.trim() === ""} onClick={(e) => makeAdmin(e, false)}>
              Remove admin
            </button>
            <button type="submit" className="btn primary" disabled={busy || adminName.trim() === ""} data-testid="admin-grant">
              Make admin
            </button>
          </div>
        </form>
      )}

      {me.isAdmin && (
        <form className="stack-form group-box" onSubmit={setCoins} noValidate data-testid="admin-player">
          <h3>Admin: player coins and items</h3>
          <label className="field">
            <span>Username</span>
            <input value={playerName} onChange={(e) => setPlayerName(e.target.value)} data-testid="admin-player-name" />
          </label>
          <label className="field">
            <span>Set coins to</span>
            <input inputMode="numeric" value={coinsText} onChange={(e) => setCoinsText(e.target.value.replace(/[^0-9]/g, ""))} data-testid="admin-coins" />
          </label>
          <div className="dialog-actions">
            <button type="submit" className="btn primary" disabled={busy || playerName.trim() === "" || !coinsValid} data-testid="admin-set-coins">
              Set coins
            </button>
          </div>
          <label className="field">
            <span>Item</span>
            <select value={itemChoice} onChange={(e) => setItemChoice(e.target.value)} data-testid="admin-item">
              <option value="all">All items</option>
              {ITEMS.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.category}: {i.name}
                </option>
              ))}
            </select>
          </label>
          <StatusLine status={playerStatus} />
          <div className="dialog-actions">
            <button type="button" className="btn" disabled={busy || playerName.trim() === ""} onClick={() => setUnlock(false)} data-testid="admin-take-item">
              Take away
            </button>
            <button type="button" className="btn primary" disabled={busy || playerName.trim() === ""} onClick={() => setUnlock(true)} data-testid="admin-give-item">
              Give
            </button>
          </div>
        </form>
      )}

      {me.isAdmin && (
        <form className="stack-form group-box" onSubmit={giveOrbs} noValidate data-testid="admin-orbs">
          <h3>Admin: give free orb pulls</h3>
          <label className="field">
            <span>Username</span>
            <input value={orbName} onChange={(e) => setOrbName(e.target.value)} data-testid="admin-orbs-name" />
          </label>
          <label className="field">
            <span>Orb</span>
            <select value={orbTier} onChange={(e) => setOrbTier(e.target.value as OrbTier)} data-testid="admin-orbs-tier">
              {ORB_TIERS.map((t) => (
                <option key={t} value={t}>
                  {ORB_NAMES[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>How many (1-{ADMIN_ORBS_MAX})</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={ADMIN_ORBS_MAX}
              value={orbCountText}
              onChange={(e) => setOrbCountText(e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="admin-orbs-count"
            />
          </label>
          <StatusLine status={orbStatus} />
          <div className="dialog-actions">
            <button type="submit" className="btn primary" disabled={busy || orbName.trim() === "" || !orbCountValid} data-testid="admin-orbs-give">
              Give
            </button>
          </div>
        </form>
      )}

      {me.isAdmin && <AdminBroadcast />}

      {me.isAdmin && <AdminStats />}
      {me.isAdmin && <AdminFeedback />}

      <form className="stack-form group-box danger-zone" onSubmit={deleteAccount} noValidate>
        <h3>Delete account</h3>
        <p className="small">This erases your account, coins and items for good.</p>
        <label className="field">
          <span>{me.hasPassword ? "Type your password to confirm" : "Type your username to confirm"}</span>
          <input
            type={me.hasPassword ? "password" : "text"}
            autoComplete="off"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            data-testid="account-delete-confirm"
          />
        </label>
        <StatusLine status={deleteStatus} />
        <div className="dialog-actions">
          <button type="submit" className="btn danger" disabled={busy || deleteConfirm === ""} data-testid="account-delete">
            Delete my account
          </button>
        </div>
      </form>
    </div>
  );
}
